import React, { useState } from 'react';
import { UserPlus, Mail, Lock, Building, Phone, ArrowRight, ArrowLeft } from 'lucide-react';
import { signUp } from '../lib/auth';
import { Button } from '@/components/ui/button';
import AuthInput from '../components/auth/AuthInput';
import AuthError from '../components/auth/AuthError';
import { cn } from '@/lib/utils';

interface RegistrationStep {
  title: string;
  description: string;
}

const steps: RegistrationStep[] = [
  {
    title: 'Company Information',
    description: 'Tell us about your business'
  },
  {
    title: 'Account Setup',
    description: 'Create your login credentials'
  }
];

const Register = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const validateStep = (step: number): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (step === 0) {
      if (!formData.companyName.trim()) {
        errors.companyName = 'Company name is required';
      }
      if (!formData.phone.trim()) {
        errors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        errors.phone = 'Please enter a valid 10-digit phone number';
      }
    }
    
    if (step === 1) {
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Format phone number
      const cleanPhone = formData.phone.replace(/\D/g, '');
      const formattedPhone = `(${cleanPhone.slice(0,3)}) ${cleanPhone.slice(3,6)}-${cleanPhone.slice(6)}`;

      await signUp(formData.email, formData.password, {
        name: formData.companyName,
        phone: formattedPhone,
      });

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg';
      successMessage.innerHTML = `
        <div class="flex flex-col">
          <span class="font-medium">Registration successful!</span>
          <span class="text-sm mt-1">Redirecting to login...</span>
        </div>
      `;
      document.body.appendChild(successMessage);

      // Redirect after delay
      setTimeout(() => {
        document.body.removeChild(successMessage);
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      console.error('Registration error:', err);
      setError(
        err instanceof Error 
          ? err.message.includes('duplicate key') 
            ? 'An account with this email already exists'
            : err.message
          : 'An error occurred during registration'
      );
      if (err instanceof Error && err.message.includes('duplicate key')) {
        setCurrentStep(1); // Go back to email step if there's a duplicate
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Column - Registration Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="inline-flex flex-col items-center justify-center mb-6">
              <div className="logo-container w-12 h-12 flex items-center justify-center mb-2">
                <span className="logo-text text-2xl">S</span>
              </div>
              <span className="text-2xl font-bold">Serial Suite</span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight">{steps[currentStep].title}</h2>
            <p className="text-sm text-muted-foreground">
              {steps[currentStep].description}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className={cn(
                "w-full border-t transition-colors",
                currentStep >= 1 ? "border-primary" : "border-muted"
              )} />
            </div>
            <div className="relative flex justify-between">
              {steps.map((step, index) => (
                <div
                  key={step.title}
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors",
                    index <= currentStep
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {index + 1}
                </div>
              ))}
            </div>
          </div>

          <AuthError message={error} />

          <form onSubmit={handleSubmit} className="space-y-6">
            {currentStep === 0 && (
              <>
                <AuthInput
                  label="Company Name"
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  icon={Building}
                  placeholder="Your Company Name"
                  error={validationErrors.companyName}
                />

                <AuthInput
                  label="Phone Number"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, phone: value });
                  }}
                  icon={Phone}
                  placeholder="(555) 123-4567"
                  error={validationErrors.phone}
                />
              </>
            )}

            {currentStep === 1 && (
              <>
                <AuthInput
                  label="Email address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  icon={Mail}
                  placeholder="name@company.com"
                  error={validationErrors.email}
                />

                <AuthInput
                  label="Password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  icon={Lock}
                  placeholder="••••••••"
                  error={validationErrors.password}
                />

                <AuthInput
                  label="Confirm Password"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  icon={Lock}
                  placeholder="••••••••"
                  error={validationErrors.confirmPassword}
                />

                <div className="flex items-center">
                  <input
                    id="terms"
                    type="checkbox"
                    required
                    className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-primary"
                  />
                  <label htmlFor="terms" className="ml-2 block text-sm text-muted-foreground">
                    I agree to the{' '}
                    <Button variant="link" className="p-0 h-auto">Terms of Service</Button>
                    {' '}and{' '}
                    <Button variant="link" className="p-0 h-auto">Privacy Policy</Button>
                  </label>
                </div>
              </>
            )}

            <div className="flex justify-between space-x-4">
              {currentStep > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(prev => prev - 1)}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              
              <Button
                type="submit"
                className={currentStep === 0 ? 'ml-auto' : ''}
              >
                {currentStep === steps.length - 1 ? (
                  loading ? 'Creating Account...' : 'Create Account'
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Button
              variant="link"
              className="font-medium"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/';
              }}
            >
              Sign in
            </Button>
          </p>
        </div>
      </div>

      {/* Right Column - Cover Image */}
      <div className="hidden lg:block relative w-0 flex-1">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&q=80"
          alt="Service professional at work"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-gray-900/10" />
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
          <blockquote className="space-y-2">
            <p className="text-lg font-medium">
              "Joining Serial Suite was the best decision for our business. The platform has everything we need to manage our service operations effectively."
            </p>
            <footer className="text-sm">
              Michael Chen • Tech Solutions Inc
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Register;